<template>
  <div>
    <b-overlay
      variant="transparent"
      overlay-class="custom-overlay-bg"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <div v-show="!showOffers">
        <div class="my-2">
          <div class="d-flex justify-content-between align-items-center">
            <h3>{{ t('Step 1: Shipment Preparation') }}</h3>
            <b-button
              variant="primary"
              :to="{ name: 'bulk_shipment_import' }"
              class="d-flex align-items-center"
            >
              <feather-icon icon="FileTextIcon" class="mr-50" />
              <span class="align-middle">{{ t('Bulk Shipment with Excel') }}</span>
            </b-button>
          </div>
          <h5 class="mt-2">{{ t('Shipment Destination') }}</h5>
          <v-select
            v-model="shippingDestination"
            :options="destinationOptions"
            :clearable="false"
            label="text"
            :reduce="option => option.value"
            class="mt-1 shipment-destination-select card"
            style="max-width: 300px"
          />
        </div>

        <!-- Alıcı Bilgileri (Consignee) -->
        <b-card class="mb-2">
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">{{ t('Consignee Informations') }}</h4>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </template>

          <b-row class="mb-2">
            <b-col md="6">
              <b-form-group label-for="consignee_address_book" class="mb-0">
                <div class="d-flex align-items-center">
                  <feather-icon icon="BookmarkIcon" size="16" class="mr-1" />
                  <v-select
                    id="consignee_address_book"
                    v-model="consigneeAddressBook"
                    :options="address_book_list"
                    :placeholder="t('Fill From Address Book')"
                    label="text"
                    class="w-100"
                    @input="setConsigneeAddressData"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group>
            <b-form-radio-group
              v-model="consigneeInformations.party_type"
              :options="[
                { text: t('Business'), value: 'business' },
                { text: t('Personal'), value: 'individual' }
              ]"
              name="consignee-type"
              class="radio-pointer"
            />
          </b-form-group>

          <validation-observer ref="consigneeInformationsForm" tag="form">
            <b-row>
              <!-- Adres Bilgileri -->
              <b-col md="6" class="border-right-md">
                <h5 class="mb-2">{{ t('Address Information') }}</h5>

                <validation-provider
                  #default="{ errors }"
                  ref="consigneeCountryValidation"
                  :name="t('Country')"
                  rules="required"
                  vid="consignee_country"
                >
                  <b-form-group label-for="consignee_country">
                    <template #label>
                      <span>{{ t('Country') }}</span>
                      <span v-if="showValidationHints && errors.length" class="required-asterisk"
                        >*</span
                      >
                    </template>
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <v-select
                          id="consignee_country"
                          v-model="consigneeInformations.country"
                          :options="countries"
                          :placeholder="t('Make a Choice')"
                          label="text"
                          @input="getConsigneeStateList"
                        >
                          <span slot="no-options">{{ t('No records found') }}</span>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </div>
                      <b-button
                        variant="primary"
                        class="ml-1"
                        style="height: fit-content"
                        @click="openPostalCodeSearch('consignee')"
                      >
                        {{ t('Address Search') }}
                      </b-button>
                    </div>
                  </b-form-group>
                </validation-provider>

                <b-row>
                  <b-col md="6">
                    <b-form-group label-for="consignee_state">
                      <template #label>
                        <span>{{ t('City') }}</span>
                      </template>
                      <b-form-input
                        id="consignee_state"
                        :value="
                          consigneeInformations.state && consigneeInformations.state.text
                            ? consigneeInformations.state.text
                            : consigneeInformations.state
                        "
                        :placeholder="t('City')"
                        disabled
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label-for="consignee_postal_code">
                      <template #label>
                        <span>{{ t('Postal Code') }}</span>
                      </template>
                      <b-form-input
                        id="consignee_postal_code"
                        :value="
                          consigneeInformations.postal_code &&
                          consigneeInformations.postal_code.combinedLabel
                            ? consigneeInformations.postal_code.combinedLabel
                            : consigneeInformations.postal_code
                        "
                        :placeholder="t('Postal Code')"
                        disabled
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <validation-provider
                  #default="{ errors }"
                  :name="t('Address')"
                  rules="required"
                  vid="consignee_address"
                >
                  <b-form-group label-for="consignee_address">
                    <template #label>
                      <span>{{ t('Address') }}</span>
                      <span v-if="showValidationHints && errors.length" class="required-asterisk"
                        >*</span
                      >
                    </template>
                    <b-form-input
                      v-model="consigneeInformations.address"
                      :placeholder="t('Address')"
                      @change="resetConsigneeAddressBook"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- İletişim Bilgileri -->
              <b-col md="6">
                <h5 class="mb-2">{{ t('Contact Information') }}</h5>

                <b-row>
                  <b-col
                    md="6"
                    v-if="!isDomesticShipment || consigneeInformations.party_type !== 'individual'"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Attention Name')"
                      :rules="consigneeInformations.name ? '' : 'required'"
                      vid="consignee_attention_name"
                    >
                      <b-form-group label-for="consignee_attention_name">
                        <template #label>
                          <span>{{ t('Attention Name') }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consigneeInformations.attention_name"
                          :placeholder="t('Attention Name')"
                          @change="resetConsigneeAddressBook"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col
                    :md="
                      isDomesticShipment && consigneeInformations.party_type === 'individual'
                        ? 12
                        : 6
                    "
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="
                        isDomesticShipment && consigneeInformations.party_type === 'individual'
                          ? t('Name Surname')
                          : t('Company Name')
                      "
                      :rules="consigneeInformations.attention_name ? '' : 'required'"
                      vid="consignee_name"
                    >
                      <b-form-group label-for="consignee_name">
                        <template #label>
                          <span>{{
                            isDomesticShipment && consigneeInformations.party_type === 'individual'
                              ? t('Name Surname')
                              : t('Company Name')
                          }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consigneeInformations.name"
                          :placeholder="
                            isDomesticShipment && consigneeInformations.party_type === 'individual'
                              ? t('Name Surname')
                              : t('Company Name')
                          "
                          @change="resetConsigneeAddressBook"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Phone Number')"
                      rules="required"
                      vid="consignee_phone_number"
                    >
                      <b-form-group label-for="consignee_phone_number">
                        <template #label>
                          <span>{{ t('Phone Number') }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consigneeInformations.phone_number"
                          :placeholder="t('Phone Number')"
                          type="number"
                          @change="resetConsigneeAddressBook"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Consignee E-Mail')"
                      :rules="
                        isDomesticShipment && consigneeInformations.party_type === 'individual'
                          ? 'email'
                          : 'required|email'
                      "
                      vid="attention_email"
                    >
                      <b-form-group label-for="attention_email">
                        <template #label>
                          <span>{{
                            t('Consignee E-Mail') +
                            (isDomesticShipment && consigneeInformations.party_type === 'individual'
                              ? ` (${t('Optional')})`
                              : '')
                          }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consigneeInformations.attention_email"
                          :placeholder="t('Consignee E-Mail')"
                          type="email"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row v-if="consigneeInformations.party_type === 'business'">
                  <b-col :md="isInternationalShipment ? 6 : 12">
                    <b-form-group :label="t('Tax Number (optional)')">
                      <b-form-input
                        v-model="consigneeInformations.tax_number"
                        :placeholder="t('Tax Number')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col v-if="isInternationalShipment" md="6">
                    <b-form-group :label="t('EORI Number (optional)')">
                      <b-form-input
                        v-model="consigneeInformations.eori_number"
                        :placeholder="t('EORI Number')"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </validation-observer>
        </b-card>

        <!-- Gönderici Bilgileri (Consignor) -->
        <b-card class="mb-2">
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">{{ t('Consignor Informations') }}</h4>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </template>

          <b-row class="mb-2">
            <b-col md="6">
              <b-form-group label-for="consignor_address_book" class="mb-0">
                <div class="d-flex align-items-center">
                  <feather-icon icon="BookmarkIcon" size="16" class="mr-1" />
                  <v-select
                    id="consignor_address_book"
                    v-model="consignorAddressBook"
                    :options="filteredConsignorAddressBookList"
                    :placeholder="t('Fill From Address Book')"
                    label="text"
                    class="w-100"
                    @input="setConsignorAddressData"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group>
            <b-form-radio-group
              v-model="consignorInformations.party_type"
              :options="[
                { text: t('Business'), value: 'business' },
                { text: t('Personal'), value: 'individual' }
              ]"
              name="consignor-type"
            />
          </b-form-group>

          <validation-observer ref="consignorInformationsForm" tag="form">
            <b-row>
              <!-- Adres Bilgileri -->
              <b-col md="6" class="border-right-md">
                <h5 class="mb-2">{{ t('Address Information') }}</h5>

                <validation-provider
                  #default="{ errors }"
                  ref="consignorCountryValidation"
                  :name="t('Country')"
                  rules="required"
                  vid="consignor_country"
                >
                  <b-form-group label-for="consignor_country">
                    <template #label>
                      <span>{{ t('Country') }}</span>
                      <span v-if="showValidationHints && errors.length" class="required-asterisk"
                        >*</span
                      >
                    </template>
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <v-select
                          id="consignor_country"
                          v-model="consignorInformations.country"
                          :options="filteredConsignorCountries"
                          :placeholder="t('Make a Choice')"
                          label="text"
                          @input="getConsignorStateList"
                        >
                          <span slot="no-options">{{ t('No records found') }}</span>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </div>
                      <b-button
                        variant="primary"
                        class="ml-1"
                        style="height: fit-content"
                        @click="openPostalCodeSearch('consignor')"
                      >
                        {{ t('Address Search') }}
                      </b-button>
                    </div>
                  </b-form-group>
                </validation-provider>

                <b-row>
                  <b-col md="6">
                    <b-form-group label-for="consignor_state">
                      <template #label>
                        <span>{{ t('City') }}</span>
                      </template>
                      <b-form-input
                        id="consignor_state"
                        :value="
                          consignorInformations.state && consignorInformations.state.text
                            ? consignorInformations.state.text
                            : consignorInformations.state
                        "
                        :placeholder="t('City')"
                        disabled
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label-for="consignor_postal_code">
                      <template #label>
                        <span>{{ t('Postal Code') }}</span>
                      </template>
                      <b-form-input
                        id="consignor_postal_code"
                        :value="
                          consignorInformations.postal_code &&
                          consignorInformations.postal_code.combinedLabel
                            ? consignorInformations.postal_code.combinedLabel
                            : consignorInformations.postal_code
                        "
                        :placeholder="t('Postal Code')"
                        disabled
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <validation-provider
                  #default="{ errors }"
                  :name="t('Address')"
                  rules="required"
                  vid="consignor_address"
                >
                  <b-form-group label-for="consignor_address">
                    <template #label>
                      <span>{{ t('Address') }}</span>
                      <span v-if="showValidationHints && errors.length" class="required-asterisk"
                        >*</span
                      >
                    </template>
                    <b-form-input
                      v-model="consignorInformations.address"
                      :placeholder="t('Address')"
                      @change="resetConsignorAddressBook"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- İletişim Bilgileri -->
              <b-col md="6">
                <h5 class="mb-2">{{ t('Contact Information') }}</h5>

                <b-row>
                  <b-col
                    md="6"
                    v-if="!isDomesticShipment || consignorInformations.party_type !== 'individual'"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Attention Name')"
                      :rules="consignorInformations.name ? '' : 'required'"
                      vid="consignor_attention_name"
                    >
                      <b-form-group label-for="consignor_attention_name">
                        <template #label>
                          <span>{{ t('Attention Name') }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consignorInformations.attention_name"
                          :placeholder="t('Attention Name')"
                          @change="resetConsignorAddressBook"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col
                    :md="
                      isDomesticShipment && consignorInformations.party_type === 'individual'
                        ? 12
                        : 6
                    "
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="
                        isDomesticShipment && consignorInformations.party_type === 'individual'
                          ? t('Name Surname')
                          : t('Company Name')
                      "
                      :rules="consignorInformations.attention_name ? '' : 'required'"
                      vid="consignor_name"
                    >
                      <b-form-group label-for="consignor_name">
                        <template #label>
                          <span>{{
                            isDomesticShipment && consignorInformations.party_type === 'individual'
                              ? t('Name Surname')
                              : t('Company Name')
                          }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consignorInformations.name"
                          :placeholder="
                            isDomesticShipment && consignorInformations.party_type === 'individual'
                              ? t('Name Surname')
                              : t('Company Name')
                          "
                          @change="resetConsignorAddressBook"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Phone Number')"
                      rules="required"
                      vid="consignor_phone_number"
                    >
                      <b-form-group label-for="consignor_phone_number">
                        <template #label>
                          <span>{{ t('Phone Number') }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consignorInformations.phone_number"
                          :placeholder="t('Phone Number')"
                          type="number"
                          @change="resetConsignorAddressBook"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Consignor E-Mail')"
                      :rules="
                        isDomesticShipment && consignorInformations.party_type === 'individual'
                          ? 'email'
                          : 'required|email'
                      "
                      vid="consignor_attention_email"
                    >
                      <b-form-group label-for="consignor_attention_email">
                        <template #label>
                          <span>{{
                            t('Consignor E-Mail') +
                            (isDomesticShipment && consignorInformations.party_type === 'individual'
                              ? ` (${t('Optional')})`
                              : '')
                          }}</span>
                          <span
                            v-if="showValidationHints && errors.length"
                            class="required-asterisk"
                            >*</span
                          >
                        </template>
                        <b-form-input
                          v-model="consignorInformations.attention_email"
                          :placeholder="t('Consignor E-Mail')"
                          type="email"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row v-if="consignorInformations.party_type === 'business'">
                  <b-col :md="isInternationalShipment ? 6 : 12">
                    <b-form-group :label="t('Tax Number (optional)')">
                      <b-form-input
                        v-model="consignorInformations.tax_number"
                        :placeholder="t('Tax Number')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col v-if="isInternationalShipment" md="6">
                    <b-form-group :label="t('EORI Number (optional)')">
                      <b-form-input
                        v-model="consignorInformations.eori_number"
                        :placeholder="t('EORI Number')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col v-if="isInternationalShipment" md="6">
                    <b-form-group :label="`${t('IOSS Number')} (${t('Optional')})`">
                      <b-form-input v-model="ioss_number" :placeholder="t('IOSS Number')" />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </validation-observer>
        </b-card>

        <!-- Paket ve Ürün Bilgileri -->
        <b-card class="mb-2">
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">{{ t('Package and Product Informations') }}</h4>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </template>

          <!-- Paket Tipi -->
          <b-row class="mb-1">
            <b-col cols="12">
              <div class="package-type-inline">
                <span class="package-type-inline__label">{{ t('Package Type') }}:</span>
                <b-form-radio-group
                  id="package_type"
                  v-model="selectedPackageType"
                  name="package-type"
                  :options="packageTypeOptions"
                  class="package-type-inline__options"
                  @change="applySelectedPackageTypeToRows"
                />
              </div>
            </b-col>
          </b-row>

          <validation-observer ref="packageInformationsValidationForm" tag="form">
            <PackageField
              v-for="item in totalPackageRow"
              :key="item"
              :ref="`packageField_${item}`"
              :index="item"
              :show-delete-button="totalPackageRow > 1"
              :show-detail="false"
              :show-post-type="false"
              :show-dangerous-goods="isInternationalShipment"
              :show-header="false"
              :is-international="isInternationalShipment"
              :is-domestic="isDomesticShipment"
              :consignment-post-type="consignmentPostType"
              :consignment-package-type="consignmentPackageType"
              @consignment-post-type-loaded="onConsignmentPostTypeLoaded"
              @consignment-package-type-loaded="onConsignmentPackageTypeLoaded"
              @deletePackage="deletePackage"
            />
          </validation-observer>
        </b-card>

        <!-- Sevkiyat Bilgileri -->
        <b-card class="mb-2">
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="mb-0">{{ t('Shipment Informations') }}</h4>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </template>

          <validation-observer ref="shipmentInformationsForm" tag="form">
            <b-row>
              <b-col cols="12" :md="isInternationalShipment ? 3 : 4" class="mb-1 mb-md-0">
                <validation-provider
                  #default="{ errors }"
                  :name="t('Shipment Type')"
                  :rules="shipmentTypeRules"
                  vid="shipment_type"
                >
                  <b-form-group label-for="shipment_type">
                    <template #label>
                      <span>
                        {{
                          `${t('Shipment Type')}${isDomesticShipment ? ` (${t('Optional')})` : ''}`
                        }}
                      </span>
                      <span v-if="showValidationHints && errors.length" class="required-asterisk"
                        >*</span
                      >
                    </template>
                    <v-select
                      id="shipment_type"
                      v-model="preference"
                      :options="shipmentTypes"
                      label="text"
                      :reduce="option => option.value"
                      :clearable="isDomesticShipment"
                      :placeholder="t('Select Shipment Type')"
                      input-id="shipment_type"
                      :class="[{ 'is-invalid': errors.length }]"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="3" class="mb-1 mb-md-0" v-if="isInternationalShipment">
                <validation-provider
                  #default="{ errors }"
                  :name="t('Incoterms Type')"
                  rules="required"
                  vid="incoterms_type"
                >
                  <b-form-group label-for="incoterms_type">
                    <template #label>
                      <span>{{ t('Incoterms Type') }}</span>
                    </template>
                    <v-select
                      id="incoterms_type"
                      v-model="incoterms_type"
                      :options="incotermsOptions"
                      label="text"
                      :reduce="option => option.value"
                      :placeholder="t('Select Incoterms Type')"
                      :class="[{ 'is-invalid': errors.length }]"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                    <div v-if="incoterms_type === 1" class="mt-50" style="font-size: 0.85rem">
                      <small class="text-danger">*</small> {{ t('Incoterms Info DDP') }}
                    </div>
                    <div
                      v-else-if="incoterms_type === 2 || incoterms_type === 3"
                      class="mt-50"
                      style="font-size: 0.85rem"
                    >
                      <small class="text-danger">*</small> {{ t('Incoterms Info DDU_DAP') }}
                    </div>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" :md="isInternationalShipment ? 3 : 4" class="mb-1 mb-md-0">
                <b-form-group
                  label-for="reference_no"
                  :label="t('Shipment Reference Number (optional)')"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Shipment Reference Number (optional)')"
                    vid="reference_no"
                  >
                    <b-form-input
                      id="reference_no"
                      v-model="reference_no"
                      :placeholder="t('Shipment Reference Number (optional)')"
                      tabindex="4"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" :md="isInternationalShipment ? 3 : 4">
                <b-form-group label-for="invoice_number" :label="t('Invoice Number (optional)')">
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Invoice Number (optional)')"
                    vid="invoice_number"
                    rules="alphaInvoice"
                  >
                    <b-form-input
                      id="invoice_number"
                      v-model="invoice_number"
                      :placeholder="t('Invoice Number (optional)')"
                      tabindex="4"
                      type="text"
                      @keydown="onKeyDown($event, 'invoice_number')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </validation-observer>
        </b-card>

        <!-- Special Services Card -->
        <consignment-special-services
          v-if="isDomesticShipment"
          :provider-id="1"
          ref="specialServices"
          @input="updateSpecialServices"
        />

        <!-- Extra Services (Document) Card -->
        <consignment-document-services
          v-if="isInternationalShipment"
          :reference-no="consignorInformations.reference_no"
          @update:documents="updateDocuments"
          @update:generateDocuments="updateGenerateDocuments"
        />

        <!-- Teklifleri Gör Butonu -->
        <b-button variant="primary" block size="lg" class="mb-3" @click="handleSeeOffers">
          {{ t('See Offers') }}
        </b-button>
      </div>

      <!-- Teklifler (Offers) -->
      <template v-if="showOffers">
        <div class="d-flex align-items-center mb-2">
          <b-button
            variant="link"
            class="p-0 d-inline-flex align-items-center"
            @click="handleBackToPreparation"
          >
            <feather-icon icon="ChevronLeftIcon" size="18" class="mr-1" />
            {{ t('Back to Shipment Preparation') }}
          </b-button>
        </div>

        <h3 class="mb-2">{{ t('Step 2: Offers') }}</h3>

        <b-card class="mb-2" :title="t('Offers')">
          <b-row v-if="providers.length > 0" ref="providersContainer" class="match-height">
            <b-col v-for="(item, index) in providers" :key="index" cols="12" md="12" lg="6">
              <ConsignmentPrice
                :data="item"
                :selected="item.id === providerId"
                :show-detail="true"
                @select="setProviderId"
              />
            </b-col>
          </b-row>
          <div class="mt-2 text-right">
            <b-button variant="success" @click="formSubmitted" :disabled="!providerId">
              {{ t('Create Consignment') }}
            </b-button>
          </div>
        </b-card>
      </template>
    </b-overlay>

    <!-- Postal Code Search Modal -->
    <PostalCodeSearchModal
      ref="postalCodeSearchModal"
      :country-iso2="postalCodeSearchCountryIso2"
      @select="handlePostalCodeSelect"
    />

    <b-modal
      id="create-consignment-success-modal"
      ref="create-consignment-success-modal"
      :hide-footer="true"
      centered
      :title="t('Successful')"
      @hide="$router.push({ name: 'consignments' })"
    >
      <h6 class="text-primary mb-2">
        {{ t('Create Consignment Success Message') }}
      </h6>

      <div class="mb-2">{{ t('Barcode') }}: {{ createConsignmentBarcode }}</div>

      <div class="create-consignment-file">
        <img v-if="createConsignmentImageFile" :src="createConsignmentImageFile" />
        <pdf v-else :src="createConsignmentPdfFile" />
      </div>

      <b-button
        variant="primary"
        type="button"
        size="md"
        class="p-5-5px mt-1"
        @click="downloadDocument"
      >
        {{ t('Download Document') }}
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BFormRadioGroup,
  BOverlay,
  BCard,
  BModal
} from 'bootstrap-vue';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper.js';
import pdf from 'vue-pdf';
import PackageField from '@/components/PackageField.vue';
import ConsignmentPrice from '@/components/ConsignmentPrice.vue';
import PostalCodeSearchModal from '@/components/PostalCodeSearchModal.vue';
import ConsignmentSpecialServices from '@/components/ConsignmentSpecialServices.vue';
import useService from '@/auth/service/useService';

export default {
  components: {
    BOverlay,
    BFormRadioGroup,
    BButton,
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    vSelect,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    PackageField,
    ConsignmentPrice,
    ConsignmentSpecialServices,
    ConsignmentDocumentServices: () => import('@/components/ConsignmentDocumentServices.vue'),
    BCard,
    pdf,
    BModal,
    PostalCodeSearchModal
  },
  watch: {
    defaultPackageValues: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            newVal.forEach((item, index) => {
              const refs = this.$refs[`packageField_${index + 1}`];
              if (refs && refs[0] && typeof refs[0].setData === 'function') {
                refs[0].setData(item);
              }
            });
          });
        }
      },
      deep: true
    },
    'consigneeInformations.country': {
      handler(newCountry) {
        this.syncShippingDestinationWithConsigneeCountry(newCountry);
      },
      deep: false
    },
    shippingDestination(newValue, oldValue) {
      if (newValue === oldValue || this.isShippingDestinationSynced) return;
      this.resetConsigneeLocationByDestinationChange();
      this.ensureShipmentTypeSelection();
    },
    'consigneeInformations.party_type': function (newType) {
      if (newType !== 'business') {
        this.consigneeInformations.tax_number = '';
        this.consigneeInformations.eori_number = '';
      }
    },
    'consignorInformations.party_type': function (newType) {
      if (newType !== 'business') {
        this.consignorInformations.tax_number = '';
        this.consignorInformations.eori_number = '';
      }
    }
  },
  mixins: [helper],
  computed: {
    ...mapGetters({
      countries: 'app/countries',
      consignorStates: 'app/consignorStates',
      consignorCities: 'app/consignorCities',
      consigneeStates: 'app/consigneeStates',
      consigneeCities: 'app/consigneeCities'
    }),
    destinationOptions() {
      return [
        { text: this.$t('Domestic'), value: 'Yurt İçi' },
        { text: this.$t('International'), value: 'Yurt Dışı' }
      ];
    },
    postalCodeOptions() {
      return this.mapToOptions(this.filteredPostalCodes);
    },
    consigneePostalCodeOptions() {
      return this.mapToOptions(this.consigneeFilteredPostalCodes);
    },
    incotermsOptions() {
      return [
        { text: 'DDP', value: 1 },
        { text: 'DDU', value: 2 },
        { text: 'DAP', value: 3 }
      ];
    },
    isInternationalShipment() {
      return this.shippingDestination === 'Yurt Dışı';
    },
    isDomesticShipment() {
      return this.shippingDestination === 'Yurt İçi';
    },
    shipmentTypeRules() {
      return this.isInternationalShipment ? 'required' : '';
    },
    filteredConsignorAddressBookList() {
      const trId = (this.countries.find(c => c.iso2 === 'TR') || {}).value;
      return this.address_book_list.filter(i => i.country && i.country.id === trId);
    },
    filteredConsignorCountries() {
      return this.countries.filter(c => c.iso2 === 'TR');
    }
  },
  data() {
    return {
      shippingDestination: 'Yurt İçi',
      showOffers: false,
      incoterms_type: null,

      // Paket Tipi (Kutu / Zarf)
      packageTypeOptions: [],
      selectedPackageType: null,
      shipmentTypes: [],
      consignmentPostType: [],
      consignmentPackageType: [],
      consignmentDocuments: [],
      consignmentGenerateDocuments: [],

      // Postal Code Search Data
      postalCodeSearchTarget: null, // 'consignor' or 'consignee'
      postalCodeSearchCountryIso2: '',

      invoice_number: '',
      ioss_number: '',
      reference_no: '',
      preference: '',
      providerId: '',
      providers: [],
      addressFieldsLocked: true,
      consignorInformations: {
        country: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: '',
        tax_number: '',
        eori_number: '',
        state_code: '',
        party_type: 'business'
      },
      consigneeInformations: {
        country: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: '',
        tax_number: '',
        eori_number: '',
        state_code: '',
        party_type: 'business'
      },
      totalPackageRow: 1,
      defaultPackageValues: [],
      submitting: false,
      createConsignmentPdfFile: '',
      createConsignmentBarcode: '',
      consigneePostalCode: [],
      postalCodes: [],
      consigneePostalCodes: [],
      minimumPostalCodeLength: 3,
      filteredPostalCodes: [],
      consigneeFilteredPostalCodes: [],
      address_book_list: [],
      consignorAddressBook: '',
      consigneeAddressBook: '',
      createConsignmentImageFile: null,
      contentType: '',
      isShippingDestinationSynced: false,
      showValidationHints: false,
      special_services: []
    };
  },
  async created() {
    await this.getAllAddressBookList();
    await this.$store.dispatch('app/getCurrencies');
    await this.$store.dispatch('app/getCountries').then(async () => {
      const priceData = this.$route.params.priceData || null;
      const copyFromId = this.$route.query.copy_from;

      if (priceData) {
        if (priceData.consignee) {
          if (priceData.consignee.address_book_data) {
            this.consigneeAddressBook = this.address_book_list.find(
              x => x.id === priceData.consignee.address_book_data.id
            );
            await this.setConsigneeAddressData(this.consigneeAddressBook);
          } else {
            this.consigneeInformations.country = this.countries.find(
              x => x.value === priceData.consignee.country
            );
            await this.getConsigneeStateList(this.consigneeInformations.country);
          }
        }

        if (priceData.packages && priceData.packages.length > 0) {
          this.totalPackageRow = priceData.packages.length || 1;
          this.selectedPackageType = priceData.packages[0]?.package_type || null;
          this.defaultPackageValues = priceData.packages || [];
        }

        if (priceData.provider_id) {
          this.setProviderId(priceData.provider_id);
        }
      } else if (copyFromId) {
        await this.loadConsignmentForCopy(copyFromId);
      }
    });
  },

  methods: {
    mapConsignmentAddress(src, key) {
      if (!src) return;
      const tgt = this[key];

      const cIso = src.countryIso2 || src.country?.iso2;
      tgt.country = this.countries.find(c => c.iso2 === cIso || c.text === src.country) || '';

      ['state', 'city'].forEach(k => {
        const val = src[k]?.name || src[k];
        if (val) tgt[k] = { text: val, value: val };
      });

      const fields = {
        name: 'name',
        attention_name: 'attention_name',
        phone: 'phone_number',
        email: 'attention_email',
        address: 'address',
        postal_code: 'postal_code',
        eori_number: 'eori_number',
        state_code: 'state_code',
        party_type: 'business'
      };
      Object.entries(fields).forEach(([k, v]) => {
        if (src[k]) tgt[v] = src[k];
      });

      tgt.tax_number = src.tax_number || src.tax_no;
    },

    async loadConsignmentForCopy(id) {
      this.submitting = true;
      try {
        const { data } = await useService.getConsignmentDetails(id);
        if (!data) return;

        ['consignor', 'consignee'].forEach(key => {
          this.mapConsignmentAddress(data[key], `${key}Informations`);
          const info = this[`${key}Informations`];
          if (info.tax_number || info.eori_number)
            this[`${key}Informations`].party_type = 'business';
        });

        this.ioss_number = data.ioss_number || '';

        const { preference, packages, incoterms_type, reference_no, invoice_number } = data;
        Object.assign(this, {
          preference: preference || packages?.[0]?.post_type || this.preference,
          incoterms_type: incoterms_type || this.incoterms_type,
          reference_no: reference_no || this.reference_no,
          invoice_number: invoice_number || this.invoice_number
        });

        if (this.consigneeInformations.country)
          this.syncShippingDestinationWithConsigneeCountry(this.consigneeInformations.country);

        if (packages?.length) {
          this.totalPackageRow = packages.length;
          await this.$nextTick();
          const packageDefaults = packages.map(pkg => {
            // eslint-disable-next-line no-unused-vars
            const { id: pkgId, package_id, tracking_code, barcode, ...rest } = pkg;
            return rest;
          });
          this.selectedPackageType = packageDefaults[0]?.package_type || null;
          this.defaultPackageValues = packageDefaults;
        }
      } catch (e) {
        this.$toast.error(e.message || this.$t('Error loading consignment details'));
      } finally {
        this.submitting = false;
      }
    },
    resetAddressData(dataKey) {
      if (!dataKey || !this[dataKey]) return;

      this[dataKey].country = '';
      this[dataKey].state = '';
      this[dataKey].city = '';
      this[dataKey].postal_code = '';
      this[dataKey].name = '';
      this[dataKey].attention_name = '';
      this[dataKey].attention_email = '';
      this[dataKey].phone_number = '';
      this[dataKey].address = '';
      this[dataKey].tax_number = '';
      this[dataKey].eori_number = '';
      this[dataKey].state_code = '';
    },
    async validateObserver(refName) {
      const observer = this.$refs[refName];

      if (!observer || typeof observer.validate !== 'function') {
        return false;
      }

      const isValid = await observer.validate();

      if (!isValid) {
        const errors = observer.errors;
        const displayedErrors = new Set();

        for (const key in errors) {
          if (errors[key] && errors[key].length > 0) {
            const msg = errors[key][0];
            if (!displayedErrors.has(msg)) {
              this.showErrorMessage(msg);
              displayedErrors.add(msg);
            }
          }
        }
      }
      return isValid;
    },
    async validatePackages() {
      const promises = [];

      for (let i = 1; i <= this.totalPackageRow; i++) {
        const childArr = this.$refs[`packageField_${i}`];
        const child = childArr && childArr[0];

        if (child && typeof child.checkValidation === 'function') {
          promises.push(child.checkValidation());
        }
      }

      if (!promises.length) {
        return false;
      }

      const results = await Promise.all(promises);
      const allValid = results.every(result => result !== false);

      // Dangerous goods seçimi yapılmadıysa (yurt dışı), inline hataya ek olarak toast da göster.
      if (!allValid && this.isInternationalShipment) {
        let hasDangerousGoodsMissing = false;

        for (let i = 1; i <= this.totalPackageRow; i++) {
          const childArr = this.$refs[`packageField_${i}`];
          const child = childArr && childArr[0];

          if (
            child &&
            child.values &&
            Object.prototype.hasOwnProperty.call(child.values, 'dangerous_goods')
          ) {
            if (child.values.dangerous_goods === null) {
              hasDangerousGoodsMissing = true;
              break;
            }
          }
        }

        if (hasDangerousGoodsMissing) {
          this.showErrorMessage(this.$t('DangerousGoodsRequired'));
        }
      }

      return allValid;
    },
    onConsignmentPackageTypeLoaded(types) {
      if (!Array.isArray(types) || !types.length) return;
      this.consignmentPackageType = types;

      const filtered = types.filter(
        x => !Array.isArray(x.groups) || !x.groups.includes('deprecated')
      );

      this.packageTypeOptions = filtered.map(x => ({
        text: x.info,
        value: x.value
      }));

      if (this.packageTypeOptions.length && !this.selectedPackageType) {
        this.selectedPackageType = this.packageTypeOptions[0].value;
        this.$nextTick(this.applySelectedPackageTypeToRows);
      }
    },
    onConsignmentPostTypeLoaded(types) {
      if (!Array.isArray(types) || !types.length) return;
      this.consignmentPostType = types;

      this.shipmentTypes = types.map(item => ({
        text: item.info,
        value: item.value,
        key: item.key
      }));

      if (!this.shipmentTypes.length) {
        this.preference = '';
        return;
      }

      const hasSelection = this.shipmentTypes.some(type => type.value === this.preference);
      if (!hasSelection) {
        this.ensureShipmentTypeSelection();
      }
    },
    applySelectedPackageTypeToRows() {
      if (!this.selectedPackageType) return;
      for (let i = 1; i <= this.totalPackageRow; i++) {
        const child = this.$refs[`packageField_${i}`];
        if (child && child[0] && typeof child[0].setData === 'function') {
          child[0].setData({ package_type: this.selectedPackageType });
        }
      }
    },
    ensurePackagesHavePostType(packages) {
      if (!Array.isArray(packages) || !packages.length) {
        return false;
      }

      packages.forEach(pkg => {
        pkg.post_type = this.preference ?? '';
      });

      if (this.isDomesticShipment) return true;

      return packages.every(pkg => pkg.post_type);
    },
    getAllAddressBookList() {
      useService
        .getAllWarehouses({
          page: 1,
          limit: 1000
        })
        .then(response => {
          this.address_book_list = response.data.map(item => {
            return {
              ...item,
              text: item.name,
              value: item.id
            };
          });
        });
    },
    ensureShipmentTypeSelection() {
      if (this.isInternationalShipment) {
        const hasSelection = this.shipmentTypes.some(type => type.value === this.preference);
        if (!hasSelection) {
          this.preference = '';
        }
      } else {
        const defaultDomestic = this.shipmentTypes.find(type => type.value === 0);
        this.preference = defaultDomestic ? defaultDomestic.value : '';
      }
    },
    openPostalCodeSearch(target) {
      const country =
        target === 'consignor'
          ? this.consignorInformations.country
          : this.consigneeInformations.country;

      if (!country) {
        this.showValidationHints = true;
        const providerRef =
          target === 'consignor'
            ? this.$refs.consignorCountryValidation
            : this.$refs.consigneeCountryValidation;
        if (providerRef && typeof providerRef.validate === 'function') {
          providerRef.validate();
        }
        this.$toast.error(this.$t('Please select the country first'));
        return;
      }

      this.postalCodeSearchTarget = target;
      this.postalCodeSearchCountryIso2 = country.iso2;
      this.$refs.postalCodeSearchModal.show();
    },
    handlePostalCodeSelect(item) {
      const targetInfo =
        this.postalCodeSearchTarget === 'consignor'
          ? this.consignorInformations
          : this.consigneeInformations;

      targetInfo.postal_code = item.postalCode;
      targetInfo.state = { text: item.state, value: item.stateCode || item.state };
      targetInfo.city = { text: item.city, value: item.city };
      targetInfo.state_code = item.stateCode;

      if (item.place) {
        targetInfo.address = item.place;
      }
    },
    syncShippingDestinationWithConsigneeCountry(country) {
      if (!country || typeof country !== 'object') {
        return;
      }

      const isoCode = (country.iso2 || '').toUpperCase();
      const targetDestination = isoCode && isoCode !== 'TR' ? 'Yurt Dışı' : 'Yurt İçi';

      if (this.shippingDestination === targetDestination) {
        return;
      }

      this.isShippingDestinationSynced = true;
      this.shippingDestination = targetDestination;
      this.$nextTick(() => {
        this.isShippingDestinationSynced = false;
      });
    },
    resetConsigneeLocationByDestinationChange() {
      this.consigneeInformations.country = '';
      this.getConsigneeStateList('');
    },
    async handleSeeOffers() {
      try {
        this.showValidationHints = true;
        const consignorValid = await this.consignorInformationsValidation();
        const consigneeValid = await this.consigneeInformationsValidation();
        const packageValid = await this.packageInformationsValidation();
        const shipmentValid = await this.validateObserver('shipmentInformationsForm');

        let specialServicesValid = true;
        if (this.isDomesticShipment && this.$refs.consignmentSpecialServices) {
          specialServicesValid = await this.$refs.consignmentSpecialServices.validate();
        }

        if (
          consignorValid &&
          consigneeValid &&
          packageValid &&
          shipmentValid &&
          specialServicesValid
        ) {
          const offersLoaded = await this.fetchProviderPrices();
          if (!offersLoaded) return;

          this.showOffers = true;

          this.$nextTick(() => {
            if (this.$refs.providersContainer) {
              this.$refs.providersContainer.scrollIntoView({ behavior: 'smooth' });
            }
          });
        }
      } catch (error) {
        this.showErrorMessage(this.$t('An error occurred'));
      }
    },
    handleBackToPreparation() {
      this.showOffers = false;
      this.providerId = '';
    },
    formSubmitted() {
      this.showValidationHints = true;
      const data = {
        consignor: {
          ...this.regionMapping(this.consignorInformations),
          party_type: this.consignorInformations.party_type
        },
        consignee: {
          ...this.regionMapping(this.consigneeInformations),
          party_type: this.consigneeInformations.party_type
        },
        // Backend expects constant values for these fields.
        preference: 0,
        address_resolver_type: 3,
        packages: []
      };

      if (this.invoice_number.trim() !== '') {
        data.invoice_number = this.invoice_number;
      }
      if (this.reference_no.trim() !== '') {
        data.reference_no = this.reference_no;
      }
      if (this.isInternationalShipment && this.ioss_number && this.ioss_number.trim() !== '') {
        data.ioss_number = this.ioss_number;
      }

      const packages = [];

      if (this.providerId) {
        data.provider_id = this.providerId;
      }

      if (this.isInternationalShipment && this.incoterms_type) {
        data.incoterms_type = this.incoterms_type;
      }

      if (this.isDomesticShipment && this.special_services && this.special_services.length) {
        data.special_services = this.special_services;
      }

      if (this.consignmentDocuments && this.consignmentDocuments.length > 0) {
        data.documents = this.consignmentDocuments;
      }

      if (this.consignmentGenerateDocuments && this.consignmentGenerateDocuments.length > 0) {
        data.generate_templated_documents = this.consignmentGenerateDocuments;
      }

      for (let i = 1; i <= this.totalPackageRow; i++) {
        packages.push(this.$refs[`packageField_${i}`][0].getData());
      }

      if (this.isDomesticShipment) {
        packages.forEach(pkg => {
          if (pkg.items && Array.isArray(pkg.items)) {
            pkg.items.forEach(item => {
              item.origin = 'TR';
            });
          }
        });
      }

      if (!this.ensurePackagesHavePostType(packages)) {
        this.showErrorMessage(this.$t('Select Shipment Type'));
        return;
      }

      data.packages = packages;

      this.submitting = true;
      useService
        .createConsignment(data)
        .then(response => {
          this.contentType = response.data.contentType;
          if (this.contentType && this.contentType.startsWith('image/')) {
            this.createConsignmentImageFile = this.getBase64ImageFile(
              response.data.base64label,
              this.contentType
            );
          } else {
            this.createConsignmentPdfFile = this.getBase64PdfFile(response.data.base64label);
          }
          this.createConsignmentBarcode = response.data.barcode;
          this.special_services = [];
          this.$refs['create-consignment-success-modal'].show();
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    downloadDocument() {
      let fileContent;
      let fileFormat;

      if (this.contentType.startsWith('image/')) {
        fileContent = this.createConsignmentImageFile;
        fileFormat = this.contentType.split('/')[1];
      } else {
        fileContent = this.createConsignmentPdfFile;
        fileFormat = 'pdf';
      }

      const fileName = `labelFile.${fileFormat}`;

      const downloadLink = document.createElement('a');
      downloadLink.href = fileContent;
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    },

    consignorInformationsValidation() {
      return this.validateObserver('consignorInformationsForm');
    },
    consigneeInformationsValidation() {
      return this.validateObserver('consigneeInformationsForm');
    },
    packageInformationsValidation() {
      return this.validatePackages();
    },

    setCurrentAddressData(data, dataKey) {
      if (!dataKey) return false;

      // vue-select clear (x) emits null. In that case we should reset related fields.
      if (!data) {
        this.resetAddressData(dataKey);
        return;
      }

      this[dataKey].tax_number = (data && (data.tax_no || data.taxNumber || data.taxNo)) || '';
      this[dataKey].eori_number =
        (data && (data.eori_number || data.eoriNumber || data.eori_no || data.eoriNo)) || '';

      if (data.country) {
        this[dataKey].country = this.countries.find(x => x.value === data.country.id);
      }

      if (data.postalCode) {
        this[dataKey].postal_code = {
          label: data.postalCode.zipcode,
          value: data.postalCode.zipcode,
          combinedLabel: `${data.postalCode.zipcode} - ${data.postalCode.place}`
        };
      }

      if (data.state) {
        this[dataKey].state = {
          text: data.state.name,
          value: data.state.id,
          stateCode: data.state.stateCode
        };
        this[dataKey].state_code = data.state.stateCode || '';
      }

      if (data.city) {
        this[dataKey].city = {
          text: data.city.name,
          value: data.city.id
        };
      }

      if (data.attentionName) {
        this[dataKey].attention_name = data.attentionName;
      }

      if (data.address) {
        this[dataKey].address = data.address;
      }

      if (data.phoneNumber) {
        this[dataKey].phone_number = data.phoneNumber;
      }

      if (data.name) {
        this[dataKey].name = data.name;
      }

      if (data.email) {
        this[dataKey].attention_email = data.email;
      }
    },

    async setConsignorAddressData(data) {
      if (!data) {
        this.resetConsignorAddressBook();
      }
      await this.setCurrentAddressData(data, 'consignorInformations');
    },

    async setConsigneeAddressData(data) {
      if (!data) {
        this.resetConsigneeAddressBook();
      }
      await this.setCurrentAddressData(data, 'consigneeInformations');
    },

    getConsignorStateList() {
      this.consignorInformations.state = '';
      this.consignorInformations.postal_code = '';

      this.postalCodes = [];
      this.filteredPostalCodes = [];

      this.resetConsignorAddressBook();

      this.getConsignorCityList('');
    },
    getConsignorCityList() {
      this.consignorInformations.city = '';
      this.$refs.consignorCityValidation.reset();

      this.resetConsignorAddressBook();
    },

    resetConsignorAddressBook() {
      this.consignorAddressBook = '';
    },
    resetConsigneeAddressBook() {
      this.consigneeAddressBook = '';
    },
    getConsigneeStateList() {
      this.consigneeInformations.state = '';
      this.consigneeInformations.postal_code = '';

      this.consigneePostalCodes = [];
      this.consigneeFilteredPostalCodes = [];

      this.resetConsigneeAddressBook();

      this.getConsigneeCityList('');
    },
    getConsigneeCityList(data) {
      this.consigneeInformations.city = '';
      if (this.$refs.consigneeCityValidation) {
        this.$refs.consigneeCityValidation.reset();
      }

      this.resetConsigneeAddressBook();

      if (data && data.value) {
        this.$store.dispatch('app/getConsigneeCities', {
          countryId: this.consigneeInformations.country.value,
          cityId: data.value
        });
      }
    },
    deletePackage(index) {
      if (this.totalPackageRow <= 1) return;

      this.$bvModal
        .msgBoxConfirm(this.$t('Delete Package Warning'), {
          title: this.$t('Delete Package'),
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          okTitle: this.$t('Delete'),
          cancelTitle: this.$t('Cancel'),
          centered: true
        })
        .then(value => {
          if (value) {
            this.removePackageByIndex(index);
          }
        });
    },
    removePackageByIndex(index) {
      const total = this.totalPackageRow;
      for (let i = index; i < total; i++) {
        const current = this.$refs[`packageField_${i}`] && this.$refs[`packageField_${i}`][0];
        const next = this.$refs[`packageField_${i + 1}`] && this.$refs[`packageField_${i + 1}`][0];
        if (current && next) {
          const nextData = next.getData(false, true);
          current.setData(nextData);
        }
      }
      this.totalPackageRow--;
    },
    async fetchProviderPrices() {
      const packages = [];

      for (let i = 1; i <= this.totalPackageRow; i++) {
        const childArr = this.$refs[`packageField_${i}`];
        const child = childArr && childArr[0];

        if (child && typeof child.getData === 'function') {
          packages.push(child.getData(true));
        }
      }

      if (!this.ensurePackagesHavePostType(packages)) {
        this.showErrorMessage(this.$t('Select Shipment Type'));
        return false;
      }

      const data = {
        consignee: {
          country: this.consigneeInformations.country.value
        },
        packages
      };

      if (this.preference !== null && this.preference !== undefined && this.preference !== '') {
        data.preference = this.preference;
      }

      this.submitting = true;

      try {
        const response = await useService.getProviderPrices(data);

        if (!response.data.length) {
          this.showErrorMessage(this.$t('No records found'));
          return false;
        }

        this.providers = this.providersCheapestMapping(response.data);
        return true;
      } catch (messages) {
        if (typeof messages === 'object' && messages && messages.length) {
          messages.forEach(item => {
            this.showErrorMessage(item.message);
          });
        }

        return false;
      } finally {
        this.submitting = false;
      }
    },
    providerSelectionValidation() {
      if (!this.providerId) {
        this.showErrorMessage(this.$t('Please Select Provider'));
        return false;
      }

      return true;
    },
    setProviderId(val) {
      this.providerId = val;
      // this.special_services = []; // Do not reset services when provider changes, as they are selected before this step
    },
    updateSpecialServices(services) {
      this.special_services = services;
    },
    updateDocuments(docs) {
      this.consignmentDocuments = docs;
    },
    updateGenerateDocuments(docs) {
      this.consignmentGenerateDocuments = docs;
    },
    handlePostalCodeSearch(postalCode) {
      if (postalCode.length >= this.minimumPostalCodeLength) {
        this.filterPostalCodes(postalCode);
      }
    },
    handleconsigneePostalCodeSearch(postalCode) {
      if (postalCode.length >= this.minimumPostalCodeLength) {
        this.filterConsigneePostalCodes(postalCode);
      }
    },
    filterPostalCodes(postalCode) {
      const data = {
        country_code: this.consignorInformations.country.iso2,
        zipcode: postalCode
      };

      useService.autoPostalCodes(data).then(response => {
        this.postalCodes = response.data;
        const filtered = this.postalCodes.filter(code => code.zipcode.includes(postalCode));

        this.filteredPostalCodes = filtered.map(code => ({
          place: code.place,
          zipcode: code.zipcode,
          state: code.state,
          city: code.province
        }));
      });
    },
    filterConsigneePostalCodes(postalCode) {
      const data = {
        country_code: this.consigneeInformations.country.iso2,
        zipcode: postalCode
      };

      useService.autoPostalCodes(data).then(response => {
        this.consigneePostalCodes = response.data;
        const filtered = this.consigneePostalCodes.filter(code =>
          code.zipcode.includes(postalCode)
        );

        this.consigneeFilteredPostalCodes = filtered.map(code => ({
          place: code.place,
          zipcode: code.zipcode,
          state: code.state,
          city: code.province
        }));
      });
    },

    mapToOptions(list) {
      return list.map(code => ({
        value: code.zipcode,
        combinedLabel: `${code.zipcode} - ${code.place}`,
        state: code.state,
        city: code.city
      }));
    },
    onKeyDown(event, field) {
      const { keyCode } = event;
      const { shiftKey } = event;
      const allowedKeys = [8, 9, 13, 46, 37, 39];
      const alphanumeric = (keyCode >= 48 && keyCode <= 57) || (keyCode >= 65 && keyCode <= 90);
      const allowDash = keyCode === 189 || keyCode === 109;

      if (field === 'invoice_number') {
        if (!alphanumeric && !allowDash && !allowedKeys.includes(keyCode)) {
          event.preventDefault();
        }
      }

      if (shiftKey && keyCode >= 48 && keyCode <= 57) {
        event.preventDefault();
      }
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.required-asterisk {
  color: #ea5455;
  margin-left: 4px;
  font-weight: 700;
}

.package-type-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.package-type-inline__label {
  font-weight: 600;
}

.package-type-inline__options {
  display: flex;
  flex-wrap: wrap;
  cursor: pointer;
  gap: 4px;

  .custom-control-label {
    cursor: pointer;
  }
}

.radio-pointer .custom-control-label {
  cursor: pointer;
}

@media (min-width: 768px) {
  .border-right-md {
    border-right: 1px solid #ebe9f1;
  }

  .dark-layout .border-right-md {
    border-right-color: rgba(255, 255, 255, 0.1);
  }
}

.shipment-destination-select .vs__open-indicator {
  fill: #6d6b72;
}

.shipment-destination-select .vs__dropdown-menu {
  top: calc(100% + 5px);
}

/* Custom Overlay Background for Theme Compatibility */
.custom-overlay-bg > div:first-child {
  background-color: #ffffff !important;
}

.dark-layout .custom-overlay-bg > div:first-child {
  background-color: #161d31 !important;
}
</style>
